import * as React from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "../ui/skeleton";

export interface SectionProps {
  /** 섹션 제목 */
  title: string;
  /** 섹션 부제목 */
  subTitle?: string;
  /** 제목 옆 안내 문구 (label-2, 기본 labels-alternative) */
  description?: React.ReactNode;
  /** 제목 크기 (normal: heading-2, small: body-1) */
  titleSize?: "normal" | "small";
  /** 자식 요소 */
  children: React.ReactNode;
  /** 추가 className */
  className?: string;
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 필수 항목 표시 (*) — FormRow 와 같은 표기 */
  required?: boolean;
}

/**
 * 폼 섹션 컴포넌트
 * - 제목과 콘텐츠를 그룹화하는 레이아웃 컴포넌트
 * - Modal, Drawer 등에서 폼 필드를 섹션별로 구분할 때 사용
 */
export function Section({
  title,
  subTitle,
  description,
  titleSize = "normal",
  children,
  className,
  isLoading = false,
  required = false,
}: SectionProps) {
  return (
    <div className={cn("flex flex-col gap-3 w-full", className)}>
      {isLoading ? (
        <Skeleton
          className={cn(
            "rounded",
            titleSize === "normal" ? "w-24 h-6" : "w-12 h-5",
          )}
        />
      ) : (
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "text-labels-primary",
              titleSize === "normal"
                ? "typo-heading-2-semibold"
                : "typo-body-1-semibold",
            )}
          >
            {title}
            {required && <span className="text-accent-mute-red">*</span>}{" "}
            <span className="text-labels-assistive">{subTitle}</span>
          </span>
          {description && (
            <span className="typo-label-2-medium text-labels-alternative">
              {description}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-col gap-2 w-full">{children}</div>
    </div>
  );
}

Section.displayName = "Section";
